/* eslint-disable no-console, no-undef */
import axios from 'axios';
import Config from 'react-native-config';
import { apiKeySelector } from 'store/appSettings/selectors';
import { ignoreResponse } from 'modules/utils/PromiseUtils';
import noop from 'lodash/noop';
import ReduxStoreContainer from 'store/ReduxStoreContainer';
import { dfdAccessTokenSelector, isDfdAuthenticatedSelector } from 'store/authentication/selectors';
import wait from 'modules/utils/wait';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import {
  AdapterSource,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherResource
} from 'modules/types/common';
import { DFDHeaders } from 'serverTypes/authentication';
import { Loggable, LoggingLevel, LogLevelKeys } from './types';

const logLevels = Object.values(LoggingLevel);

let logLevel: LogLevelKeys = 'warn';
if (Config.LOG_LEVEL && LoggingLevel[Config.LOG_LEVEL as LogLevelKeys]) {
  logLevel = Config.LOG_LEVEL as LogLevelKeys;
} else if (Config.NODE_ENV === 'development') {
  logLevel = 'log' as LogLevelKeys;
}

const PREFIX = Config.NODE_ENV === 'development' ? `dev.web.` : `web.`;

const newLoggerEnabled = Config.NEW_LOGGER === 'enabled';

const LOGGER_URL = getDispatcherUrl(
  DispatcherCategoryService.UTILITY,
  DispatcherAuthPrefix.PUBLIC,
  DispatcherResource.LOGGERS
);

const graylogAxiosClient = axios.create({
  baseURL: newLoggerEnabled ? Config.DISPATCHER_BASE_URL : Config.LOGGING_BASE_URL,
  responseType: 'json',
  timeout: Config.REQUEST_TIMEOUT_DURATION || 60000
});

/**
 * Logs a message/data to the graylog service
 * @param level logging level
 * @param message message to log
 * @param data contextual data to pass along with the message
 *
 * Note: setTimeout to prevent - Error: You may not call store.getState() while the reducer is executing. The reducer has already received the state as an argument. Pass it down from the top reducer instead of reading it from the store.
 */
export const log = async (type: LoggingLevel, message: string, data?: any): Promise<any> => {
  await wait(0); // Stick it on the end of the stack after any dispatch action has finished.

  const state = ReduxStoreContainer.getState();
  const apiKey = apiKeySelector(state);
  const isAuthed = isDfdAuthenticatedSelector(state);

  if (!apiKey) {
    return Promise.resolve();
  }

  const accessToken = dfdAccessTokenSelector(state);
  const url = newLoggerEnabled ? LOGGER_URL : '/logs';
  const authHeaders = {
    [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
    [DFDHeaders.authorization]: `Bearer ${accessToken}`
  };
  const newHeaders = newLoggerEnabled ? authHeaders : {};

  return graylogAxiosClient.post(url, [{ type, message, data, auth: isAuthed }], {
    headers: {
      apiKey,
      ...newHeaders
    }
  });
};

export function allowLog(type: LogLevelKeys): boolean {
  if (logLevel) {
    const idx = logLevels.findIndex(level => level === LoggingLevel[type]);
    const logLevelIndex = logLevels.findIndex(
      level => level === LoggingLevel[logLevel as LogLevelKeys]
    );
    return idx >= logLevelIndex;
  }
  return true;
}

/**
 * Creates a development logger.
 *
 * Logs to the console
 */
export const developmentLogger = (): Loggable => ({
  log: (message, data) => allowLog('log') && console.log(PREFIX + message, data),
  debug: (message, data) => allowLog('debug') && console.debug(PREFIX + message, data),
  info: (message, data) => allowLog('info') && console.info(PREFIX + message, data),
  warn: (message, data) => allowLog('warn') && console.log(PREFIX + message, data),
  error: (message, data) => allowLog('error') && console.log(PREFIX + message, data),
  fatal: (message, data) => console.log(PREFIX + message, data)
});

/**
 * Creates a graylog logger.
 *
 * Logs to graylog any messages. Currently we only report warning/error/fatal level messages
 */
export const graylogLogger = (): Loggable => ({
  log: noop,
  debug: noop,
  info: noop,
  warn: (message, data) => ignoreResponse(log(LoggingLevel.warn, PREFIX + message, data)),
  error: (message, data) => ignoreResponse(log(LoggingLevel.error, PREFIX + message, data)),
  fatal: (message, data) => ignoreResponse(log(LoggingLevel.fatal, PREFIX + message, data))
});

export const createLogger = (dev: boolean): Loggable => {
  const streams: Loggable[] = [graylogLogger()];

  if (dev) {
    streams.push(developmentLogger());
  }

  return {
    log: (...args) => streams.forEach(logger => logger.log(...args)),
    debug: (...args) => streams.forEach(logger => logger.debug(...args)),
    info: (...args) => streams.forEach(logger => logger.info(...args)),
    warn: (...args) => streams.forEach(logger => logger.warn(...args)),
    error: (...args) => streams.forEach(logger => logger.error(...args)),
    fatal: (...args) => streams.forEach(logger => logger.fatal(...args))
  };
};

const Logger = createLogger(process.env.NODE_ENV === 'development');
export default Logger;
